import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { CacheKey } from '../types/cache-keys';
import {
  actOnLabAssistCase,
  assignLabPod,
  attachLabScenarioItems,
  claimLabPod,
  compileLabScenario,
  deleteLabRow,
  detachLabScenarioItem,
  enrollLabAgent,
  evaluateLabRun,
  fetchLabAgents,
  fetchLabAssignments,
  fetchLabAssistCase,
  fetchLabAssistCases,
  fetchLabConfiguration,
  fetchLabConfigurationItem,
  fetchLabConfigurationItems,
  fetchLabConfigurations,
  fetchLab,
  fetchLabDataCenters,
  fetchLabDeviceAddresses,
  fetchLabDevices,
  fetchLabScenarioDeviceLinks,
  fetchLabDeviceTypes,
  fetchLabEvent,
  fetchLabEvents,
  fetchLabExecutionMethods,
  fetchLabInjection,
  fetchLabInjectionResults,
  fetchLabInjections,
  fetchLabPool,
  fetchLabPools,
  fetchLabProducts,
  fetchLabRun,
  fetchLabRuns,
  fetchLabRunTriggers,
  fetchLabs,
  fetchLabScenario,
  fetchLabAttachments,
  fetchLabScenarioItems,
  fetchLabScenarioLinks,
  fetchLabScenarios,
  fetchLabScenarioTriggers,
  fetchLabSessionHealth,
  fetchLabSessions,
  fetchLabSweeps,
  fireLabTrigger,
  injectLabStep,
  LabAgent,
  LabAgentEnrollment,
  LabAgentTransport,
  LabAssignment,
  LabAssistAction,
  LabAssistCase,
  LabAssistSeverity,
  LabCompileResult,
  LabConfiguration,
  LabConfigurationItem,
  LabCriterionState,
  LabDataCenter,
  LabDeviceType,
  LabEvent,
  LabExecutionMethod,
  LabInjection,
  LabInjectionFilters,
  LabInjectionPreview,
  LabInjectionRequest,
  LabInjectionResult,
  LabInjectionVerdict,
  LabItemAttachPair,
  LabItemAttachResult,
  LabItemDetachResult,
  Lab,
  LabDevice,
  LabDeviceAddress,
  LabScenarioDeviceLink,
  LabScenarioLink,
  LabPool,
  LabPoolSnapshot,
  LabRun,
  LabRunFilters,
  LabRunTrigger,
  LabScenario,
  LabScenarioItem,
  LabScenarioTrigger,
  LabSessionHealth,
  LabSession,
  LabSweep,
  LAB_TABLES,
  openLabAssistCase,
  previewLabInjection,
  reapplyLabInjection,
  releaseLabAssignment,
  reportLabCriterion,
  revokeLabAgent,
  saveLabRow,
  shareLabSession,
  startLabRun,
  startSelfServiceLab,
  stockLabPool,
} from '../support/labService';

/**
 * Lab data is live pod state, same reasoning as `DCLOUD_QUERY_OPTIONS`: a five-minute
 * stale time would show a step as pending for four and a half minutes after it succeeded.
 * `retry: false` because a 403 on somebody else's pool is an answer, not a hiccup worth
 * asking three times.
 */
const LAB_QUERY_OPTIONS = {
  staleTime: 1000 * 20,
  gcTime: 1000 * 60 * 5,
  retry: false as const,
};

/**
 * Authored rows change when somebody edits them, not on their own, so they get the
 * ordinary Toolbox lifetime instead of the live one.
 */
const LAB_AUTHORING_QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 10,
  retry: false as const,
};

/** A run in flight is worth polling; a finished one is not. */
const RUN_POLL_MS = 10000;

const isRunOpen = (run?: LabRun): boolean =>
  !!run && !['passed', 'failed', 'error', 'cancelled', 'expired'].includes(run.state);

// -----------------------------------------------------------------------------
// Sessions and health
// -----------------------------------------------------------------------------

export const useLabSessions = (
  activeOnly = false,
  options?: Record<string, any>,
): UseQueryResult<LabSession[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabSessions, activeOnly],
    queryFn: () => fetchLabSessions(activeOnly),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabSessionHealth = (
  sessionKey?: string,
  options?: Record<string, any>,
): UseQueryResult<LabSessionHealth, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabSessionHealth, sessionKey],
    queryFn: () => fetchLabSessionHealth(sessionKey as string),
    ...{
        ...LAB_QUERY_OPTIONS,
        enabled: !!sessionKey,
        // Health is what somebody stares at while a pod is misbehaving.
        refetchInterval: 15000,
        ...options,
      },
  });

export const useShareLabSession = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { session: LabSession; recorded: string[] },
    Error,
    { sessionKey: string; emails: string[] }
  >({
    mutationFn: ({ sessionKey, emails }) => shareLabSession(sessionKey, emails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabSessions] });
    },
  });
};

// -----------------------------------------------------------------------------
// Runs
// -----------------------------------------------------------------------------

export const useLabRuns = (
  filters: LabRunFilters = {},
  options?: Record<string, any>,
): UseQueryResult<LabRun[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabRuns, filters],
    queryFn: () => fetchLabRuns(filters),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabRun = (
  runId?: number,
  options?: Record<string, any>,
): UseQueryResult<LabRun, unknown> =>
  useQuery<LabRun, unknown>({
    queryKey: [CacheKey.LabRun, runId],
    queryFn: () => fetchLabRun(runId as number),
    ...{
      ...LAB_QUERY_OPTIONS,
      enabled: !!runId,
      refetchInterval: (query) => (isRunOpen(query.state.data) ? RUN_POLL_MS : false),
      ...options,
    },
  });

/**
 * Invalidation for anything that changes a run. The run detail is written straight from the
 * response because the endpoints all return the recomputed run - refetching to learn what
 * we were just told would flash stale numbers on the way.
 */
const useRunMutationHandlers = () => {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      onRun: (run: LabRun) => {
        if (run?.id) queryClient.setQueryData([CacheKey.LabRun, run.id], run);
        queryClient.invalidateQueries({ queryKey: [CacheKey.LabRuns] });
        queryClient.invalidateQueries({ queryKey: [CacheKey.LabAssignments] });
      },
      invalidateRun: (runId: number) => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.LabRun, runId] });
        queryClient.invalidateQueries({ queryKey: [CacheKey.LabRunTriggers, runId] });
      },
    }),
    [queryClient],
  );
};

export const useStartLabRun = () => {
  const { onRun } = useRunMutationHandlers();

  return useMutation<
    LabRun,
    Error,
    { session_key: string; scenario: string; for_user_email?: string }
  >({
    mutationFn: (payload) => startLabRun(payload),
    onSuccess: onRun,
  });
};

/**
 * Recompute, or re-open validation and recompute.
 *
 * `revalidate` is a separate argument rather than the default because it resets the
 * validation steps so they run against the pod again. That is what you want after
 * somebody fixed the thing that was failing, and not what you want by accident.
 */
export const useEvaluateLabRun = () => {
  const { onRun } = useRunMutationHandlers();

  return useMutation<LabRun, Error, { runId: number; revalidate?: boolean }>({
    mutationFn: ({ runId, revalidate }) => evaluateLabRun(runId, !!revalidate),
    onSuccess: onRun,
  });
};

export const useReportLabCriterion = () => {
  const { onRun } = useRunMutationHandlers();

  return useMutation<
    LabRun,
    Error,
    {
      runId: number;
      criterionKey: string;
      state?: LabCriterionState;
      waive?: boolean;
      reason?: string;
      detail?: Record<string, any>;
    }
  >({
    mutationFn: ({ runId, criterionKey, ...payload }) =>
      reportLabCriterion(runId, criterionKey, payload),
    onSuccess: onRun,
  });
};

export const useLabRunTriggers = (
  runId?: number,
  options?: Record<string, any>,
): UseQueryResult<LabRunTrigger[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabRunTriggers, runId],
    queryFn: () => fetchLabRunTriggers(runId as number),
    ...{
      ...LAB_QUERY_OPTIONS,
      enabled: !!runId,
      ...options,
    },
  });

export const useFireLabTrigger = () => {
  const { invalidateRun } = useRunMutationHandlers();

  return useMutation<LabRunTrigger, Error, { runId: number; triggerKey: string }>({
    mutationFn: ({ runId, triggerKey }) => fireLabTrigger(runId, triggerKey),
    onSuccess: (_result, { runId }) => invalidateRun(runId),
  });
};

// -----------------------------------------------------------------------------
// Agents
// -----------------------------------------------------------------------------

export const useLabAgents = (options?: Record<string, any>): UseQueryResult<LabAgent[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabAgents],
    queryFn: () => fetchLabAgents(),
    ...{
      ...LAB_QUERY_OPTIONS,
      refetchInterval: 30000,
      ...options,
    },
  });

/**
 * Enroll an agent.
 *
 * The response carries the only copy of the token that will ever exist - the backend keeps
 * a SHA-256 digest and nothing else. It is deliberately *not* written into the query cache:
 * a secret in the cache is a secret in a devtools panel and in whatever the cache gets
 * persisted to. The caller shows it once and drops it.
 */
export const useEnrollLabAgent = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LabAgentEnrollment,
    Error,
    {
      name: string;
      transport: LabAgentTransport;
      session_key?: string;
      scenario?: string;
      poll_interval_seconds?: number;
      lease_seconds?: number;
      capabilities?: string[];
    }
  >({
    mutationFn: (payload) => enrollLabAgent(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabAgents] });
    },
  });
};

export const useRevokeLabAgent = () => {
  const queryClient = useQueryClient();

  return useMutation<LabAgent, Error, { agentKey: string; reason?: string }>({
    mutationFn: ({ agentKey, reason }) => revokeLabAgent(agentKey, reason || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabAgents] });
    },
  });
};

// -----------------------------------------------------------------------------
// Pools and assignments
// -----------------------------------------------------------------------------

export const useLabPools = (
  includeAll = false,
  options?: Record<string, any>,
): UseQueryResult<LabPool[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabPools, includeAll],
    queryFn: () => fetchLabPools(includeAll),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabPool = (
  slug?: string,
  options?: Record<string, any>,
): UseQueryResult<LabPool, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabPool, slug],
    queryFn: () => fetchLabPool(slug as string),
    ...{
      ...LAB_QUERY_OPTIONS,
      enabled: !!slug,
      ...options,
    },
  });

const usePoolMutationHandlers = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CacheKey.LabPools] });
    queryClient.invalidateQueries({ queryKey: [CacheKey.LabPool] });
    queryClient.invalidateQueries({ queryKey: [CacheKey.LabAssignments] });
  }, [queryClient]);
};

export const useStockLabPool = () => {
  const invalidate = usePoolMutationHandlers();

  return useMutation<
    { added: number; already_present: number; snapshot: LabPoolSnapshot },
    Error,
    string
  >({
    mutationFn: (slug) => stockLabPool(slug),
    onSuccess: invalidate,
  });
};

/** Claims for the caller. There is no assignee argument, and that is the point. */
export const useClaimLabPod = () => {
  const invalidate = usePoolMutationHandlers();

  return useMutation<
    LabAssignment,
    Error,
    { slug: string; display_name?: string; team_label?: string }
  >({
    mutationFn: ({ slug, ...payload }) => claimLabPod(slug, payload),
    onSuccess: invalidate,
  });
};

/** Assigns to somebody else. Pool stewards only; the backend checks, not this hook. */
export const useAssignLabPod = () => {
  const invalidate = usePoolMutationHandlers();

  return useMutation<
    LabAssignment,
    Error,
    { slug: string; session_key: string; email: string; display_name?: string; team_label?: string }
  >({
    mutationFn: ({ slug, ...payload }) => assignLabPod(slug, payload),
    onSuccess: invalidate,
  });
};

export const useStartSelfServiceLab = () => {
  const invalidate = usePoolMutationHandlers();

  return useMutation<LabAssignment, Error, { session_key: string; display_name?: string }>({
    mutationFn: (payload) => startSelfServiceLab(payload),
    onSuccess: invalidate,
  });
};

export const useLabAssignments = (
  liveOnly = false,
  options?: Record<string, any>,
): UseQueryResult<LabAssignment[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabAssignments, liveOnly],
    queryFn: () => fetchLabAssignments(liveOnly),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

export const useReleaseLabAssignment = () => {
  const invalidate = usePoolMutationHandlers();

  return useMutation<LabAssignment, Error, { assignmentId: number; reason?: string }>({
    mutationFn: ({ assignmentId, reason }) => releaseLabAssignment(assignmentId, reason || ''),
    onSuccess: invalidate,
  });
};

// -----------------------------------------------------------------------------
// Assist
// -----------------------------------------------------------------------------

export const useLabAssistCases = (
  queueOnly = false,
  options?: Record<string, any>,
): UseQueryResult<LabAssistCase[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabAssistCases, queueOnly],
    queryFn: () => fetchLabAssistCases(queueOnly),
    ...{
      ...LAB_QUERY_OPTIONS,
      // Somebody is blocked in a lab while this list is stale.
      refetchInterval: 20000,
      ...options,
    },
  });

export const useLabAssistCase = (
  reference?: string,
  options?: Record<string, any>,
): UseQueryResult<LabAssistCase, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabAssistCase, reference],
    queryFn: () => fetchLabAssistCase(reference as string),
    ...{
      ...LAB_QUERY_OPTIONS,
      enabled: !!reference,
      ...options,
    },
  });

const useAssistMutationHandlers = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (assistCase: LabAssistCase) => {
      if (assistCase?.reference) {
        queryClient.setQueryData([CacheKey.LabAssistCase, assistCase.reference], assistCase);
      }
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabAssistCases] });
    },
    [queryClient],
  );
};

export const useOpenLabAssistCase = () => {
  const onCase = useAssistMutationHandlers();

  return useMutation<
    LabAssistCase,
    Error,
    {
      session_key: string;
      subject: string;
      detail?: string;
      severity?: LabAssistSeverity;
      device_key?: string;
      run_id?: number;
    }
  >({
    mutationFn: (payload) => openLabAssistCase(payload),
    onSuccess: onCase,
  });
};

/**
 * The five case actions in one mutation, because they all return the same case and share
 * the same invalidation. Note what is missing: `state_change`, `access_granted` and
 * `access_revoked` are not authorable event kinds. The service writes those, so that a
 * caller cannot post "access revoked" while their access is still live.
 */
export const useLabAssistAction = () => {
  const onCase = useAssistMutationHandlers();

  return useMutation<LabAssistCase, Error, { reference: string; action: LabAssistAction }>({
    mutationFn: ({ reference, action }) => actOnLabAssistCase(reference, action),
    onSuccess: onCase,
  });
};

// -----------------------------------------------------------------------------
// Fleet sweeps
// -----------------------------------------------------------------------------

export const useLabSweeps = (
  limit = 20,
  options?: Record<string, any>,
): UseQueryResult<LabSweep[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabSweeps, limit],
    queryFn: () => fetchLabSweeps(limit),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

// -----------------------------------------------------------------------------
// Step injection
// -----------------------------------------------------------------------------

export const useLabInjections = (
  filters: LabInjectionFilters = {},
  options?: Record<string, any>,
): UseQueryResult<LabInjection[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabInjections, filters],
    queryFn: () => fetchLabInjections(filters),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabInjection = (
  injectionId?: number,
  options?: Record<string, any>,
): UseQueryResult<LabInjection, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabInjection, injectionId],
    queryFn: () => fetchLabInjection(injectionId as number),
    ...{
      ...LAB_QUERY_OPTIONS,
      enabled: !!injectionId,
      ...options,
    },
  });

export const useLabInjectionResults = (
  injectionId?: number,
  verdict?: LabInjectionVerdict,
  options?: Record<string, any>,
): UseQueryResult<LabInjectionResult[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabInjectionResults, injectionId, verdict],
    queryFn: () => fetchLabInjectionResults(injectionId as number, verdict),
    ...{
        ...LAB_QUERY_OPTIONS,
        enabled: !!injectionId,
        ...options,
      },
  });

/**
 * The dry run. A mutation rather than a query because it is a POST and because caching
 * "which pods this would reach" is wrong - the answer changes as runs progress, and a
 * stale one is exactly the answer you must not act on.
 */
export const usePreviewLabInjection = () =>
  useMutation<LabInjectionPreview, Error, LabInjectionRequest>({
    mutationFn: (payload) => previewLabInjection(payload),
  });

export const useInjectLabStep = () => {
  const queryClient = useQueryClient();

  return useMutation<LabInjection, Error, LabInjectionRequest>({
    mutationFn: (payload) => injectLabStep(payload),
    onSuccess: (injection) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabInjections] });
      if (injection?.id) {
        queryClient.setQueryData([CacheKey.LabInjection, injection.id], injection);
      }
      // Injected steps show up as new run steps.
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabRuns] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabRun] });
    },
  });
};

/**
 * Re-sweep an injection.
 *
 * This is not a retry of the runs that errored - it re-evaluates every open run, which is
 * how a pod that started after the first sweep gets the step. Runs that already have it
 * come back `already_present`.
 */
export const useReapplyLabInjection = () => {
  const queryClient = useQueryClient();

  return useMutation<LabInjection, Error, number>({
    mutationFn: (injectionId) => reapplyLabInjection(injectionId),
    onSuccess: (injection) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabInjections] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabInjectionResults, injection?.id] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.LabRuns] });
    },
  });
};

// -----------------------------------------------------------------------------
// Authoring (db/lab-*)
// -----------------------------------------------------------------------------

/**
 * The labs. The top of the authored tree, and what the library view lists.
 *
 * List rows arrive without their sections nested, so counts on a list come from the detail row
 * or from the link table - not from a number the list carries, because there isn't one.
 */
export const useLabs = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<Lab[], unknown> =>
  useQuery({
    queryKey: [CacheKey.Lab, 'list', params],
    queryFn: () => fetchLabs(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

/** Nests the sections and the publications, which together are what a lab is. */
export const useLab = (
  labId?: number | string,
  options?: Record<string, any>,
): UseQueryResult<Lab, unknown> =>
  useQuery({
    queryKey: [CacheKey.Lab, labId],
    queryFn: () => fetchLab(labId as number | string),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      enabled: !!labId,
      ...options,
    },
  });

/**
 * Link rows as rows.
 *
 * The nesting on a lab and on a scenario covers reading. This is for the third question - which
 * labs use this scenario, asked from a screen that has neither loaded - and for reordering, where
 * the rows are what gets written.
 */
export const useLabScenarioLinks = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabScenarioLink[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenarioLink, 'list', params],
    queryFn: () => fetchLabScenarioLinks(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

/**
 * Address overrides, for one publication or across all of them.
 *
 * Also nested on the configuration. Fetched here when the override editor is the screen: it has to
 * list every device in every section of the lab, including the ones with no override row - which
 * are by definition absent from this result.
 */
export const useLabDeviceAddresses = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabDeviceAddress[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabDeviceAddress, 'list', params],
    queryFn: () => fetchLabDeviceAddresses(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

/**
 * The device library.
 *
 * The builder reads a section's devices nested on the scenario instead. Both exist because they
 * answer different questions, and a library list cannot be assembled from a scenario it has not
 * loaded. There is deliberately no `scenario` param to pass here: neither generic filter engine
 * crosses the link table and an unknown parameter is ignored rather than refused, so asking that
 * way returns the whole library while looking like it worked.
 */
export const useLabDevices = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabDevice[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabDevice, 'list', params],
    queryFn: () => fetchLabDevices(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

/**
 * Device attachments, as rows.
 *
 * For the editing the nesting cannot serve: detaching one section's device, or disabling the
 * attachment without detaching it. Reading in either direction is better served by the nested
 * lists - `LabScenario.devices` one way, `LabDevice.scenario_links` the other.
 */
export const useLabScenarioDeviceLinks = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabScenarioDeviceLink[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenarioDeviceLink, 'list', params],
    queryFn: () => fetchLabScenarioDeviceLinks(params),
    ...{
        ...LAB_AUTHORING_QUERY_OPTIONS,
        ...options,
      },
  });

export const useLabScenarios = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabScenario[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenario, 'list', params],
    queryFn: () => fetchLabScenarios(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

/** Nests devices, steps and success criteria. Triggers are a separate table, fetched below. */
export const useLabScenario = (
  scenarioId?: number | string,
  options?: Record<string, any>,
): UseQueryResult<LabScenario, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenario, scenarioId],
    queryFn: () => fetchLabScenario(scenarioId as number | string),
    ...{
        ...LAB_AUTHORING_QUERY_OPTIONS,
        enabled: !!scenarioId,
        ...options,
      },
  });

export const useLabScenarioTriggers = (
  scenarioId?: number,
  options?: Record<string, any>,
): UseQueryResult<LabScenarioTrigger[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenarioTrigger, scenarioId],
    queryFn: () => fetchLabScenarioTriggers(scenarioId as number),
    ...{
        ...LAB_AUTHORING_QUERY_OPTIONS,
        enabled: !!scenarioId,
        ...options,
      },
  });

/**
 * The two reference catalogues an item is authored against.
 *
 * Long lifetimes because they are the kind of table somebody adds a row to when a new platform
 * shows up, not something that changes while a screen is open. Both are fetched whole: every
 * item form needs all of them to render five selects, and a filtered page cannot say which
 * methods a device type accepts.
 */
export const useLabDeviceTypes = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabDeviceType[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabDeviceType, 'list', params],
    queryFn: () => fetchLabDeviceTypes(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabExecutionMethods = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabExecutionMethod[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabExecutionMethod, 'list', params],
    queryFn: () => fetchLabExecutionMethods(params),
    ...{ ...LAB_AUTHORING_QUERY_OPTIONS, ...options },
  });

/**
 * Cisco products, for pointing a device type at one.
 *
 * Two thousand rows that change on a release cadence, read by one select in one modal, so it is
 * fetched only while that modal is open (`enabled`) and held for the session once it has been.
 */
export const useLabProducts = (
  options?: Record<string, any>,
): UseQueryResult<{ id: number; name: string }[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabProducts],
    queryFn: () => fetchLabProducts(),
    ...{
      staleTime: 1000 * 60 * 30,
      gcTime: 1000 * 60 * 60,
      retry: false as const,
      ...options,
    },
  });

/**
 * The catalogue of configuration items.
 *
 * Fetched whole rather than searched server-side: every screen that attaches an item needs the
 * prerequisite graph to say what a choice will bring in, and that cannot be answered from a
 * filtered page. The catalogue is authored content, so it is small and it changes when somebody
 * edits it.
 */
export const useLabConfigurationItems = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabConfigurationItem[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabConfigurationItem, 'list', params],
    queryFn: () => fetchLabConfigurationItems(params),
    ...{ ...LAB_AUTHORING_QUERY_OPTIONS, ...options },
  });

export const useLabConfigurationItem = (
  itemId?: number | string,
  options?: Record<string, any>,
): UseQueryResult<LabConfigurationItem, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabConfigurationItem, itemId],
    queryFn: () => fetchLabConfigurationItem(itemId as number | string),
    ...{
        ...LAB_AUTHORING_QUERY_OPTIONS,
        enabled: !!itemId,
        ...options,
      },
  });

/** One scenario's attached items, in compile order. Also nested on the scenario detail row. */
export const useLabScenarioItems = (
  scenarioId?: number,
  options?: Record<string, any>,
): UseQueryResult<LabScenarioItem[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenarioItem, scenarioId],
    queryFn: () => fetchLabScenarioItems(scenarioId as number),
    ...{
        ...LAB_AUTHORING_QUERY_OPTIONS,
        enabled: !!scenarioId,
        ...options,
      },
  });

/**
 * Every attachment, across every scenario, for the library's usage lists.
 *
 * Separate from `useLabScenarioItems` and cached separately, because the two answer opposite
 * questions and the per-scenario key would make one look like a stale version of the other. This is
 * how "which scenarios use this item" and "which items land on this device" are answered on a
 * screen that has loaded neither a scenario nor a device.
 */
export const useLabAttachments = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabScenarioItem[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabScenarioItem, 'list', params],
    queryFn: () => fetchLabAttachments(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

/**
 * Invalidation shared by the three item actions.
 *
 * All three reach further than the row they were called on. Attaching writes rows nobody asked
 * for, compiling rewrites steps and criteria and can mark devices monitored, and detaching
 * prunes prerequisites - so the scenario, its steps, its criteria and its devices all go stale
 * together, and refreshing only the item list would leave the screen half true.
 */
const useLabItemInvalidation = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    [
      CacheKey.LabScenarioItem,
      CacheKey.LabConfigurationItem,
      CacheKey.LabScenario,
      CacheKey.LabScenarioStep,
      CacheKey.LabSuccessCriterion,
      CacheKey.LabDevice,
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  }, [queryClient]);
};

/**
 * Attach items to devices in a scenario, with their prerequisites.
 *
 * The result is worth keeping rather than discarding: `added_as_prerequisites` is the list of
 * things the author did not ask for, and telling them beats letting eleven rows appear. Each
 * entry there names the device it landed on, since a prerequisite inherits one and that is the
 * part worth checking.
 */
export const useAttachLabScenarioItems = () => {
  const invalidate = useLabItemInvalidation();

  return useMutation<
    LabItemAttachResult,
    Error,
    {
      scenarioId: number;
      items: LabItemAttachPair[];
      optionOverrides?: Record<string, Record<string, any>>;
    }
  >({
    mutationFn: ({ scenarioId, items, optionOverrides }) =>
      attachLabScenarioItems(scenarioId, items, optionOverrides || {}),
    onSuccess: invalidate,
  });
};

/** Detach one item, and any prerequisite left with nothing to serve. `pruned` says which. */
export const useDetachLabScenarioItem = () => {
  const invalidate = useLabItemInvalidation();

  return useMutation<
    LabItemDetachResult,
    Error,
    { scenarioId: number; attachmentId: number }
  >({
    mutationFn: ({ scenarioId, attachmentId }) => detachLabScenarioItem(scenarioId, attachmentId),
    onSuccess: invalidate,
  });
};

/**
 * Compile the attached items into steps and success criteria.
 *
 * Not called automatically after an attach: option values are normally set between the two, and
 * compiling early would generate steps against the defaults and then have to rewrite them.
 */
export const useCompileLabScenario = () => {
  const invalidate = useLabItemInvalidation();

  return useMutation<LabCompileResult, Error, number>({
    mutationFn: (scenarioId) => compileLabScenario(scenarioId),
    onSuccess: invalidate,
  });
};

/**
 * The per-data-centre bindings.
 *
 * `scenario` narrows to one lab's publications; passing nothing lists every published lab,
 * which is what the admin catalogue wants. Filtering by `data_center` answers the other
 * question - what is published in London - and both go straight to the generic CRUD API.
 */
export const useLabConfigurations = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabConfiguration[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabConfiguration, 'list', params],
    queryFn: () => fetchLabConfigurations(params),
    ...{
      ...LAB_AUTHORING_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabConfiguration = (
  configurationId?: number | string,
  options?: Record<string, any>,
): UseQueryResult<LabConfiguration, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabConfiguration, configurationId],
    queryFn: () => fetchLabConfiguration(configurationId as number | string),
    ...{
        ...LAB_AUTHORING_QUERY_OPTIONS,
        enabled: !!configurationId,
        ...options,
      },
  });

/**
 * The data centres a lab can be published in.
 *
 * Reference data that changes when somebody adds a data centre, which is to say almost never,
 * so it gets a long lifetime rather than the authoring one.
 */
export const useLabDataCenters = (
  options?: Record<string, any>,
): UseQueryResult<LabDataCenter[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabDataCenters],
    queryFn: () => fetchLabDataCenters(),
    ...{
      staleTime: 1000 * 60 * 30,
      gcTime: 1000 * 60 * 60,
      retry: false as const,
      ...options,
    },
  });

/**
 * Events.
 *
 * These sit between authoring and operations: the row is authored, but a running event's pools
 * are not, and a proctor watching the list wants the pool counts to move. Hence the live
 * lifetime rather than the authoring one.
 */
export const useLabEvents = (
  params: Record<string, any> = {},
  options?: Record<string, any>,
): UseQueryResult<LabEvent[], unknown> =>
  useQuery({
    queryKey: [CacheKey.LabEvent, 'list', params],
    queryFn: () => fetchLabEvents(params),
    ...{
      ...LAB_QUERY_OPTIONS,
      ...options,
    },
  });

export const useLabEvent = (
  eventId?: number | string,
  options?: Record<string, any>,
): UseQueryResult<LabEvent, unknown> =>
  useQuery({
    queryKey: [CacheKey.LabEvent, eventId],
    queryFn: () => fetchLabEvent(eventId as number | string),
    ...{
      ...LAB_QUERY_OPTIONS,
      enabled: !!eventId,
      ...options,
    },
  });

/** Which cache keys a table's rows live under, so a save invalidates the right lists. */
const AUTHORING_CACHE_KEYS: Record<string, CacheKey> = {
  [LAB_TABLES.lab]: CacheKey.Lab,
  [LAB_TABLES.scenarioLink]: CacheKey.LabScenarioLink,
  [LAB_TABLES.deviceAddress]: CacheKey.LabDeviceAddress,
  [LAB_TABLES.scenario]: CacheKey.LabScenario,
  [LAB_TABLES.configuration]: CacheKey.LabConfiguration,
  [LAB_TABLES.event]: CacheKey.LabEvent,
  [LAB_TABLES.device]: CacheKey.LabDevice,
  [LAB_TABLES.scenarioDeviceLink]: CacheKey.LabScenarioDeviceLink,
  [LAB_TABLES.step]: CacheKey.LabScenarioStep,
  [LAB_TABLES.criterion]: CacheKey.LabSuccessCriterion,
  [LAB_TABLES.trigger]: CacheKey.LabScenarioTrigger,
  [LAB_TABLES.configurationItem]: CacheKey.LabConfigurationItem,
  [LAB_TABLES.scenarioItem]: CacheKey.LabScenarioItem,
  [LAB_TABLES.deviceType]: CacheKey.LabDeviceType,
  [LAB_TABLES.executionMethod]: CacheKey.LabExecutionMethod,
  [LAB_TABLES.pool]: CacheKey.LabPools,
  [LAB_TABLES.reportSink]: CacheKey.LabReportSink,
};

/**
 * Which other lists a table's rows appear inside, because the serializers nest and flatten.
 *
 * A step edit has to invalidate the scenario, which nests it. A configuration edit has to
 * invalidate both the scenario (which nests configurations) and events (whose serializer
 * flattens the configuration's data centre, demo and size into every row) - otherwise an
 * event list keeps showing the size the lab used to be published at. Pools nest under their
 * event for the same reason.
 */
const AUTHORING_DEPENDENT_KEYS: Record<string, CacheKey[]> = {
  // A lab edit reaches its publications, which flatten its name, and the events built on those.
  [LAB_TABLES.lab]: [CacheKey.LabConfiguration, CacheKey.LabEvent],
  // A link is read from both ends and nested on both. Attaching a section to a lab changes what
  // that lab delivers *and* the scenario's "Used In" count, which is the number an admin checks
  // before editing the scenario - a stale one there is a change made on a false premise.
  [LAB_TABLES.scenarioLink]: [CacheKey.Lab, CacheKey.LabScenario],
  // An override is nested on its configuration, and the configuration is nested on the lab.
  [LAB_TABLES.deviceAddress]: [CacheKey.LabConfiguration, CacheKey.Lab],
  [LAB_TABLES.scenario]: [],
  // Also the lab: a publication is nested on it, and the lab library counts data centres off
  // that array rather than off a separate number.
  [LAB_TABLES.configuration]: [CacheKey.Lab, CacheKey.LabScenario, CacheKey.LabEvent],
  [LAB_TABLES.event]: [CacheKey.LabPools],
  [LAB_TABLES.pool]: [CacheKey.LabEvent, CacheKey.LabScenario],
  // An item edit reaches every scenario that attached it - the attachment list flattens the
  // item's name, operation and pre_configure flag into each row. It does *not* change the
  // generated steps: those are only rewritten by a compile, which is the whole point of
  // compiling being a separate act.
  [LAB_TABLES.configurationItem]: [CacheKey.LabScenarioItem, CacheKey.LabScenario],
  [LAB_TABLES.scenarioItem]: [CacheKey.LabScenario],
  // A device type edit changes what the item catalogue and the device list read - both flatten
  // its key and name - and adding a method to it changes which items may be attached where.
  [LAB_TABLES.deviceType]: [
    CacheKey.LabConfigurationItem,
    CacheKey.LabDevice,
    CacheKey.LabScenario,
  ],
  // A device is shared, so an edit is never local: every section attached to it nests the same
  // row, and every publication's override list flattens the address being overridden.
  [LAB_TABLES.device]: [CacheKey.LabScenario, CacheKey.LabDeviceAddress],
  // An attachment is nested on both ends - the section's device list and the device's own
  // "used in" list - and it changes which items may be attached in that section, so the
  // attachment list goes with it.
  [LAB_TABLES.scenarioDeviceLink]: [
    CacheKey.LabScenario,
    CacheKey.LabDevice,
    CacheKey.LabScenarioItem,
  ],
  // A method edit reaches every item that uses it in any of the four roles, and the device
  // types that list it. It does *not* rewrite generated steps: only a compile does that.
  [LAB_TABLES.executionMethod]: [CacheKey.LabConfigurationItem, CacheKey.LabDeviceType],
};

const useAuthoringInvalidation = (table: string) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const key = AUTHORING_CACHE_KEYS[table];
    if (key) queryClient.invalidateQueries({ queryKey: [key] });

    const dependents = AUTHORING_DEPENDENT_KEYS[table] ?? [CacheKey.LabScenario];
    dependents.forEach((dependent) => queryClient.invalidateQueries({ queryKey: [dependent] }));
  }, [queryClient, table]);
};

/** Save an authored row. See `AUTHORING_DEPENDENT_KEYS` for what else goes stale with it. */
export const useSaveLabRow = <T extends { id?: number }>(table: string) => {
  const invalidate = useAuthoringInvalidation(table);

  return useMutation<T, Error, T>({
    mutationFn: (row) => saveLabRow<T>(table, row),
    onSuccess: invalidate,
  });
};

export const useDeleteLabRow = (table: string) => {
  const invalidate = useAuthoringInvalidation(table);

  return useMutation<boolean, Error, number>({
    mutationFn: (id) => deleteLabRow(table, id),
    onSuccess: invalidate,
  });
};

/**
 * Attach scenarios to a lab as sections.
 *
 * Several at once because that is how the picker is used - an admin composing a lab chooses the
 * three sections it is made of - and because doing it one save at a time would leave a lab
 * half-composed if the second call failed, with no signal about which half.
 *
 * Ordered from the end of what is already there. Not renumbered: the existing sections keep the
 * positions somebody chose, and inserting between them is a reorder, which is a different act.
 *
 * Unlike attaching a configuration item, nothing is expanded: a scenario has no prerequisites, so
 * what is asked for is exactly what is written.
 */
export const useAttachLabScenarios = () => {
  const invalidate = useAuthoringInvalidation(LAB_TABLES.scenarioLink);

  return useMutation<
    LabScenarioLink[],
    Error,
    { labId: number; scenarioIds: number[]; startOrder?: number }
  >({
    mutationFn: async ({ labId, scenarioIds, startOrder = 0 }) =>
      Promise.all(
        scenarioIds.map((scenario, index) =>
          saveLabRow<Partial<LabScenarioLink> & { id?: number }>(LAB_TABLES.scenarioLink, {
            lab: labId,
            scenario,
            // Ten apart, so a later insert between two sections has somewhere to go without
            // rewriting every row after it.
            order: startOrder + index * 10,
            enabled: true,
            notes: '',
          })
        ) as Promise<LabScenarioLink>[]
      ),
    onSuccess: invalidate,
  });
};

/**
 * Detach one section from one lab.
 *
 * Deletes the link, never the scenario. The distinction is the whole point of the tier: the
 * scenario is shared, so removing it here must leave every other lab that uses it untouched -
 * and the API refuses to delete a scenario that any link still points at, which is the backstop
 * if this is ever called with the wrong id.
 */
export const useDetachLabScenario = () => {
  const invalidate = useAuthoringInvalidation(LAB_TABLES.scenarioLink);

  return useMutation<boolean, Error, number>({
    mutationFn: (linkId) => deleteLabRow(LAB_TABLES.scenarioLink, linkId),
    onSuccess: invalidate,
  });
};

/**
 * Write a new section order.
 *
 * Every affected row is sent rather than only the moved one, because `order` is a position among
 * siblings: moving section three to the top changes what one and two mean too, and sending one
 * row would leave two sections claiming the same position, which the API accepts and the runtime
 * then breaks the tie on by id.
 */
export const useReorderLabScenarios = () => {
  const invalidate = useAuthoringInvalidation(LAB_TABLES.scenarioLink);

  return useMutation<LabScenarioLink[], Error, LabScenarioLink[]>({
    mutationFn: async (links) =>
      Promise.all(
        links.map((link, index) =>
          saveLabRow<Partial<LabScenarioLink> & { id?: number }>(LAB_TABLES.scenarioLink, {
            id: link.id,
            lab: link.lab,
            scenario: link.scenario,
            order: index * 10,
            enabled: link.enabled,
            notes: link.notes || '',
          })
        ) as Promise<LabScenarioLink>[]
      ),
    onSuccess: invalidate,
  });
};

/**
 * Attach devices to a section.
 *
 * The same shape as `useAttachLabScenarios` one tier down, and with the same point to it: the
 * device row is not touched, so a box already used by four other sections is unchanged by being
 * used by a fifth. No `order` in the payload, deliberately - devices are ordered by the library
 * row, because sections run in sequence and devices do not, and a per-section device order would
 * be a second position deciding nothing.
 */
export const useAttachLabDevices = () => {
  const invalidate = useAuthoringInvalidation(LAB_TABLES.scenarioDeviceLink);

  return useMutation<
    LabScenarioDeviceLink[],
    Error,
    { scenarioId: number; deviceIds: number[] }
  >({
    mutationFn: async ({ scenarioId, deviceIds }) =>
      Promise.all(
        deviceIds.map((device) =>
          saveLabRow<Partial<LabScenarioDeviceLink> & { id?: number }>(
            LAB_TABLES.scenarioDeviceLink,
            { scenario: scenarioId, device, enabled: true, notes: '' }
          )
        ) as Promise<LabScenarioDeviceLink>[]
      ),
    onSuccess: invalidate,
  });
};

/**
 * Detach one device from one section.
 *
 * Deletes the attachment, never the device. Which matters more here than it does for sections: a
 * device is shared by default rather than by choice - every pod is the same template - so a
 * delete would take a box out from under labs whose authors never saw this screen. The API's
 * backstop is `PROTECT` on the items attached to it.
 */
export const useDetachLabDevice = () => {
  const invalidate = useAuthoringInvalidation(LAB_TABLES.scenarioDeviceLink);

  return useMutation<boolean, Error, number>({
    mutationFn: (linkId) => deleteLabRow(LAB_TABLES.scenarioDeviceLink, linkId),
    onSuccess: invalidate,
  });
};

/**
 * Make a device's sections match a chosen set, in one call.
 *
 * For the device editor, where attachment is a list of checkboxes rather than one act: the form
 * hands back what should be true and this works out the difference. Attaching and detaching are
 * separately available above for the builder tree, where each is a deliberate single action and
 * a diff would be a worse description of what the admin did.
 *
 * Returns what changed rather than the rows, because the caller's next move is to say so in a
 * toast - "attached to two sections, detached from one" - and it cannot get that from the rows.
 */
export const useSyncLabDeviceScenarios = () => {
  const invalidate = useAuthoringInvalidation(LAB_TABLES.scenarioDeviceLink);

  return useMutation<
    { attached: number[]; detached: number[] },
    Error,
    { deviceId: number; existing: LabScenarioDeviceLink[]; scenarioIds: number[] }
  >({
    mutationFn: async ({ deviceId, existing, scenarioIds }) => {
      const wanted = new Set(scenarioIds);
      const held = new Set(existing.map((link) => link.scenario));

      const toAttach = scenarioIds.filter((id) => !held.has(id));
      const toDetach = existing.filter((link) => !wanted.has(link.scenario));

      await Promise.all([
        ...toAttach.map((scenario) =>
          saveLabRow<Partial<LabScenarioDeviceLink> & { id?: number }>(
            LAB_TABLES.scenarioDeviceLink,
            { scenario, device: deviceId, enabled: true, notes: '' }
          )
        ),
        ...toDetach.map((link) => deleteLabRow(LAB_TABLES.scenarioDeviceLink, link.id)),
      ]);

      return { attached: toAttach, detached: toDetach.map((link) => link.scenario) };
    },
    onSuccess: invalidate,
  });
};
